import {
  generateFileTypeEnum,
  getFileExtension,
  getGroupFromFileType,
} from "../../utils/fileUploadHelpers";
import {
  groups as FileFormatGroups,
  zippableSoftwareExtentions,
  zippableSoftwareFolders,
} from "../../content/workFileFormatsGroups";
import { FOLDER_PACKAGE_UPLOAD_KIND } from "../../utils/dropzone/dropzoneUtils";
import { notEmpty } from "../../utils/basic";
import { computeComponentVersion } from "../../components/Component/helpers";
import get from "lodash/get";
import { v4 as uuid } from "uuid";
import { createEntity } from "../../data/delicDataClient";
import { getLastNumber } from "../../components/Component/helpers";
import sortBy from "lodash/sortBy";
import {
  getFileCategoryType,
  RECORDING,
  VIDEO,
  isSourceFile,
} from "../../utils/fileUtils";

const MAX_GRAPHQL_INT = 2147483647;
const FOLDER_UPLOAD_MIME_TYPE = "application/zip";

function getSafeGraphqlInt(value) {
  const numberValue = Number(value) || 0;
  return numberValue <= MAX_GRAPHQL_INT ? numberValue : null;
}

function getDroppedFilePath(file) {
  return file.path || file.webkitRelativePath || file.name || "";
}

function getPathSegments(filePath) {
  return String(filePath || "")
    .split("/")
    .filter((segment) => segment.length);
}

function getFolderUploadRoot(fileWrap) {
  if (!fileWrap || fileWrap.kind === FOLDER_PACKAGE_UPLOAD_KIND) return "";
  const pathSegments = getPathSegments(getDroppedFilePath(fileWrap.file || {}));
  if (pathSegments.length < 2) return "";
  return pathSegments[0];
}

function getFolderRelativePath(fileWrap) {
  const pathSegments = getPathSegments(getDroppedFilePath(fileWrap.file || {}));
  if (pathSegments.length < 2) return "";
  return pathSegments.slice(1).join("/");
}

function getFolderMarkerExtension(folderRoot, fileWraps) {
  const folderExtension = (folderRoot.match(/(\.)\w+/g) || []).pop();
  if (
    folderExtension &&
    zippableSoftwareFolders.some((ext) => ext === folderExtension.toLowerCase())
  ) {
    return folderExtension.toLowerCase();
  }

  for (const fileWrap of fileWraps) {
    const fileExtension = getFileExtension(get(fileWrap, "file.name", ""), "");
    if (
      fileExtension &&
      zippableSoftwareExtentions.some(
        (ext) => ext === fileExtension.toLowerCase()
      )
    ) {
      return fileExtension.toLowerCase();
    }
  }

  return ".zip";
}

function getUploadEntries(files) {
  const entries = [];
  const folderEntriesByRoot = {};

  files.forEach((fileWrap, index) => {
    const folderRoot = getFolderUploadRoot(fileWrap);
    if (!folderRoot) {
      entries.push({ kind: "file", fileWrap, fileIndex: index });
      return;
    }

    if (!folderEntriesByRoot[folderRoot]) {
      folderEntriesByRoot[folderRoot] = {
        kind: "folder",
        folderRoot,
        fileWraps: [],
        fileIndexes: [],
      };
      entries.push(folderEntriesByRoot[folderRoot]);
    }

    folderEntriesByRoot[folderRoot].fileWraps.push(fileWrap);
    folderEntriesByRoot[folderRoot].fileIndexes.push(index);
  });

  return entries;
}

export function getUploadedFileUris(uploadedFile) {
  const childFileInputs = get(uploadedFile, "payload.fileInputs", []);
  const childUris = childFileInputs
    .map((fileInput) => fileInput && fileInput.uri)
    .filter((uri) => Boolean(uri));
  const legacyUri = get(uploadedFile, "payload.fileInput.uri", "");
  return legacyUri ? [legacyUri].concat(childUris) : childUris;
}

export function getUploadPrimaryUri(uploadedFile) {
  return get(getUploadedFileUris(uploadedFile), "[0]", "");
}

export function getUploadComponentId(uploadedFile) {
  return get(uploadedFile, "payload.componentInput.id", "");
}

export function getUploadCorrelationKey(uploadedFile) {
  return getUploadComponentId(uploadedFile) || getUploadPrimaryUri(uploadedFile);
}

const COMPONENT_RESULT_KEYS = [
  "CreateComponent",
  "CreateVersionOfComponent",
  "createComponentWithWork",
  "CreateVersionOfComponentWithWork",
];

export function getComponentFromMutationResult(result) {
  if (!result) return undefined;

  if (Array.isArray(result)) {
    return getComponentFromMutationResult(get(result, "[0]"));
  }

  if (get(result, "id", "")) {
    return result;
  }

  const data = get(result, "data");
  const wrapper = data || result;
  return COMPONENT_RESULT_KEYS.reduce((component, key) => {
    return component || getComponentFromMutationResult(get(wrapper, key));
  }, undefined);
}

export function findComponentById(components, componentId) {
  if (!componentId) return undefined;
  return (components || []).reduce((foundComponent, result) => {
    if (foundComponent) return foundComponent;
    const component = getComponentFromMutationResult(result);
    return get(component, "id", "") === componentId ? component : undefined;
  }, undefined);
}

export function componentHasFileUri(component, uri) {
  if (!uri) return false;
  const unwrappedComponent = getComponentFromMutationResult(component);
  return (get(unwrappedComponent, "file", []) || []).some(
    (file) => file && file.uri === uri
  );
}

export function findComponentByFileUri(components, uri) {
  return (components || []).reduce((foundComponent, result) => {
    if (foundComponent) return foundComponent;
    const component = getComponentFromMutationResult(result);
    return componentHasFileUri(component, uri) ? component : undefined;
  }, undefined);
}

export function findComponentForUpload(components, uploadedFile) {
  return (
    findComponentById(components, getUploadComponentId(uploadedFile)) ||
    getUploadedFileUris(uploadedFile).reduce((foundComponent, uri) => {
      return foundComponent || findComponentByFileUri(components, uri);
    }, undefined)
  );
}

export function findTransferResultForInput(results, input, index) {
  const orderedResult = get(results, `[${index}]`);
  const orderedComponent = getComponentFromMutationResult(orderedResult);
  if (orderedComponent) {
    return orderedComponent;
  }
  return findComponentForUpload(results, input);
}

export function buildGroupedComponentsFromTransferResults({
  transferInputs = [],
  results = [],
  groupedComponents = {},
}) {
  return transferInputs.reduce((arr, input, index) => {
    const component = findTransferResultForInput(results, input, index);
    if (!component) return arr;

    const groupedComponent = groupedComponents[getUploadCorrelationKey(input)];
    if (!groupedComponent) return arr;

    return arr.concat({ ...groupedComponent, component: [component] });
  }, []);
}

export function getReferencesFromTransferResults({
  transferInputs = [],
  results = [],
}) {
  return transferInputs.reduce((references, input, index) => {
    if (!input.isIdea) return references;

    const reference = findTransferResultForInput(results, input, index);
    return reference ? references.concat(reference) : references;
  }, []);
}

/*
Get all the components either in a project or in all the projects and dashboard
This is used in the add files and manage files
 */
export function getSameSpaceGroupedComponents(
  newProjectId,
  allProjectsGroupedComponents
) {
  let groupedComponents = [];

  const project =
    allProjectsGroupedComponents &&
    allProjectsGroupedComponents.find((proj) => proj.id === newProjectId);
  if (project) {
    project.components.forEach((comp) => {
      groupedComponents.push({
        ...comp,
        projectId: project.id,
        projectName: project.title,
      });
    });
  }

  return groupedComponents.map((groupedComp) => {
    const comp = get(groupedComp, "component[0]", groupedComp);
    const groupLabels = {
      componentGroup: get(groupedComp, "componentGroup", 0),
      componentGroupOrderWeight: get(
        groupedComp,
        "componentGroupOrderWeight",
        0
      ),
      componentOrderWeight: get(groupedComp, "componentOrderWeight", 0),
      projectId: get(groupedComp, "projectId", "Dashboard"),
      projectName: get(groupedComp, "projectName", "Dashboard"),
    };
    return { ...comp, groupLabels };
  });
}

/*
Create the groups of components, the result is an object with the groups as key.
If it's the dashboard is not going to create it
This is needed when adding them to get the proper group and version
 */
export function getComponentsInSelectedProjectByGroupAndOrdered({
  projectId,
  groupedComponents,
}) {
  if (projectId === "Dashboard") return {};
  const groups = {};
  groupedComponents.forEach((componentWithLabel) => {
    if (groups[componentWithLabel.groupLabels.componentGroup]) {
      groups[componentWithLabel.groupLabels.componentGroup].push(
        componentWithLabel
      );
    } else {
      groups[componentWithLabel.groupLabels.componentGroup] = [
        componentWithLabel,
      ];
    }
  });
  Object.keys(groups).forEach((key) => {
    const ordered = sortBy(groups[key], [
      function(o) {
        return -o.groupLabels.componentOrderWeight;
      },
    ]);
    groups[key] = ordered;
  });

  return groups;
}

export function getLatestVersionsOfComponents(newSameSpaceGroupedComponents) {
  const latestVersions = [];
  newSameSpaceGroupedComponents.forEach((comp) => {
    if (notEmpty(comp.versions)) {
      const sortedVersionsLatestFirst = [...comp.versions].sort((a, b) => { return getLastNumber(b.version) - getLastNumber(a.version) })
      latestVersions.push({
        ...sortedVersionsLatestFirst[0],
        groupLabels: comp.groupLabels,
      });
    } else {
      latestVersions.push(comp);
    }
  });
  return latestVersions;
}

//
/*
This is to get the versions options for each uploaded file,
it's created here since we need to get this list when uploading new files in case it's a version of a component
 */
export function getSelectableVersionsOptions(
  newProjectId,
  newLatestVersionsOfComponents
) {
  const options = [
    {
      value: "",
      label: "No version",
      type: "None",
      projectId: newProjectId,
      projectName: "",
      version: "",
    },
  ];

  newLatestVersionsOfComponents.forEach((comp) => {
    options.push({
      value: comp.id,
      label: comp.title,
      type: comp["__typename"],
      projectId: get(comp, "groupLabels.projectId", "Dashboard"),
      projectName: get(comp, "groupLabels.projectName", "Dashboard"),
      version: `${getLastNumber(comp.version)}`,
    });
  });

  return options;
}

function getTitle(fileName, fileExtension, mimeType) {
  if (!fileExtension) return fileName;

  if (fileName.endsWith(".zip") && mimeType === "application/zip") {
    if (zippableSoftwareFolders.some((ext) => ext === fileExtension)) {
      return fileName.substring(0, fileName.indexOf(fileExtension));
    }
    return fileName.substring(0, fileName.indexOf(".zip"));
  }
  return fileName.substring(0, fileName.indexOf(fileExtension));
}

const getComponentGroup = (fileExtension, mimeType) => {
  let componentGroup = getGroupFromFileType(fileExtension, mimeType);
  if (!componentGroup) {
    const mimeTypeIsAudio =
      mimeType.includes("audio") || mimeType.includes("video");
    if (mimeTypeIsAudio) {
      componentGroup = FileFormatGroups.AUDIO_FILE;
    }
  }
  return componentGroup;
};

export function getPayloads({
  files,
  personId,
  targetParentComponent,
  targetLatestVersionComponent,
}) {
  return getUploadEntries(files).reduce((arr, uploadEntry) => {
    if (uploadEntry.kind === "folder") {
      const { folderRoot, fileWraps, fileIndexes } = uploadEntry;
      const folderUploadId = uuid();
      const folderItemCount = fileWraps.length;
      const rawTotalBytes = fileWraps.reduce(
        (total, fileWrap) => total + (Number(get(fileWrap, "file.size", 0)) || 0),
        0
      );
      const fileExtension = getFolderMarkerExtension(folderRoot, fileWraps);
      const fileExtensionEnum = generateFileTypeEnum(fileExtension);
      const componentGroup = getComponentGroup(
        fileExtension,
        FOLDER_UPLOAD_MIME_TYPE
      );
      const componentInput = {
        title: folderRoot && folderRoot.trim(),
        version: "1",
        isFolderUpload: true,
        folderUploadId,
        folderRoot,
        folderItemCount,
        folderTotalBytes: String(rawTotalBytes),
      };
      const fileInput = {
        fileName: folderRoot,
        fileType: fileExtensionEnum,
        mimeType: FOLDER_UPLOAD_MIME_TYPE,
        size: getSafeGraphqlInt(rawTotalBytes),
      };
      const fileInputs = fileWraps.map((fileWrap) => {
        const childFile = fileWrap.file || {};
        const childFileName = childFile.name || "";
        const childFileExtension = getFileExtension(childFileName, fileWrap.ext);
        return {
          fileName: childFileName,
          fileType: generateFileTypeEnum(childFileExtension),
          mimeType: childFile.type || "",
          size: getSafeGraphqlInt(childFile.size),
          relativePath: getFolderRelativePath(fileWrap),
          folderRoot,
          folderUploadId,
        };
      });
      const payload = {
        partyId: personId,
        projectId: "",
        componentInput,
        fileInput,
        fileInputs,
        componentGroup: componentGroup.trim(),
        componentGroupOrderWeight: 0,
        componentOrderWeight: 0,
      };
      const fileCategoryType = getFileCategoryType(
        fileInput.mimeType,
        fileInput.fileType
      );

      let input = {
        payload,
        sourceFile: false,
        storageInput: null,
        fileInput,
        type: fileCategoryType,
        isUploading: false,
        isUploaded: false,
        isUploadFailed: false,
        uploadError: "",
        uploadLoadedBytes: 0,
        uploadTotalBytes: rawTotalBytes,
        uploadProgress: 0,
        uploadAttemptId: "",
        retryUnavailable: false,
        isIdea: false,
        idForComparison: uuid(),
        label: fileCategoryType,
        isWorkRequired:
          fileCategoryType === RECORDING || fileCategoryType === VIDEO,
        isFolderUpload: true,
        folderFileIndexes: fileIndexes,
      };

      if (targetParentComponent && targetLatestVersionComponent) {
        input.payload.parentComponentId = get(targetParentComponent, "id", "");
        input.payload.workId = get(targetParentComponent, "work[0].id", "");
        input.payload.latestVersionComponentId = targetLatestVersionComponent.id;
        input.payload.componentInput.version = computeComponentVersion(
          targetLatestVersionComponent.version
        ).increase;
        input.previousTitle = input.payload.componentInput.title;
        input.payload.componentInput.title = get(
          targetParentComponent,
          "title",
          ""
        );
        input.previousGroup = input.payload.componentGroup;
        input.payload.componentGroup = get(
          targetParentComponent,
          "componentGroup",
          ""
        );
        input.payload.componentGroupOrderWeight = get(
          targetParentComponent,
          "componentGroupOrderWeight",
          ""
        );
        input.payload.componentOrderWeight = get(
          targetParentComponent,
          "componentOrderWeight",
          ""
        );
        input.isVersion = true;
      }

      return arr.concat(input);
    }

    const fileWrap = uploadEntry.fileWrap;
    if (fileWrap.kind === FOLDER_PACKAGE_UPLOAD_KIND) {
      const manifest = fileWrap.manifest || {};
      const folderName = manifest.folderName || fileWrap.folderName || "Folder";
      const fileName = folderName.endsWith(".zip") ? folderName : folderName + ".zip";
      const mimeType = "application/zip";
      const fileExtension = fileWrap.ext || ".zip";
      const fileExtensionEnum = generateFileTypeEnum(fileExtension);
      const title = getTitle(fileName, fileExtension, mimeType);
      const componentGroup = getComponentGroup(fileExtension, mimeType);
      const rawTotalBytes = Number(manifest.totalBytes) || 0;
      const componentInput = {
        title: title && title.trim(),
        version: "1",
      };
      const fileInput = {
        fileName,
        fileType: fileExtensionEnum,
        mimeType,
        size: getSafeGraphqlInt(rawTotalBytes),
        folderPackageStatus: "READY_TO_UPLOAD_RAW_FILES",
        folderPackageItemCount: manifest.itemCount,
        folderPackageTotalBytes: String(manifest.totalBytes || 0),
        folderPackageFolderName: folderName,
      };
      const payload = {
        partyId: personId,
        projectId: "",
        componentInput,
        fileInput,
        componentGroup: componentGroup.trim(),
        componentGroupOrderWeight: 0,
        componentOrderWeight: 0,
      };

      return arr.concat({
        payload,
        sourceFile: true,
        storageInput: null,
        fileInput,
        type: "SourceFile",
        isUploading: false,
        isUploaded: false,
        isUploadFailed: false,
        uploadError: "",
        uploadLoadedBytes: 0,
        uploadTotalBytes: rawTotalBytes,
        uploadProgress: 0,
        uploadAttemptId: "",
        retryUnavailable: false,
        isIdea: false,
        idForComparison: uuid(),
        fileIndex: uploadEntry.fileIndex,
        label: "SourceFile",
        isWorkRequired: false,
        isFolderPackage: true,
        folderPackagingStatus: "READY_TO_UPLOAD_RAW_FILES",
        folderPackagingJob: {
          jobId: uuid(),
          manifest,
          folderName,
          classification: manifest.classification,
          itemCount: manifest.itemCount,
          totalBytes: String(manifest.totalBytes || 0),
          dawMarker: manifest.dawMarker || null,
        },
      });
    }

    const notSerializableFile = fileWrap.file;
    const file = {
      size: notSerializableFile.size,
      lastModified: notSerializableFile.lastModified,
      type: notSerializableFile.type,
      name: notSerializableFile.name,
      path: notSerializableFile.path,
    };
    const fileName = file.name || "";
    const mimeType = file.type || "";
    const fileExtension = getFileExtension(fileName, fileWrap.ext);
    const fileExtensionEnum = generateFileTypeEnum(fileExtension);
    const title = getTitle(fileName, fileExtension, mimeType);
    const componentGroup = getComponentGroup(fileExtension, mimeType);
    const isFileSourceFile = isSourceFile({
      file: [{ fileType: fileExtensionEnum, mimeType }],
    });

    const metadata = {
      contentLength: file.size.toString(),
      lastModified: file.lastModified.toString(),
    };
    const storageInput = {
      file,
      metadata,
      contentType: file.type,
    };

    const componentInput = {
      title: title && title.trim(),
      version: "1",
    };
    const fileInput = {
      fileName: file.name,
      fileType: fileExtensionEnum,
      mimeType: file.type,
      size: file.size,
    };

    const payload = {
      partyId: personId,
      projectId: "",
      componentInput,
      fileInput,
      componentGroup: componentGroup.trim(),
      componentGroupOrderWeight: 0,
      componentOrderWeight: 0,
    };

    const fileCategoryType = getFileCategoryType(
      fileInput.mimeType,
      fileInput.fileType,
      fileInput.fileName
    );
    const type = fileCategoryType;

    let input = {
      payload,
      sourceFile: isFileSourceFile,
      storageInput,
      fileInput,
      type,
      isUploading: false,
      isUploaded: false,
      isUploadFailed: false,
      uploadError: "",
      uploadLoadedBytes: 0,
      uploadTotalBytes: file.size || 0,
      uploadProgress: 0,
      uploadAttemptId: "",
      retryUnavailable: false,
      isIdea: false,
      idForComparison: uuid(),
      label: fileCategoryType,
      fileIndex: uploadEntry.fileIndex,
      isWorkRequired:
        fileCategoryType === RECORDING || fileCategoryType === VIDEO,
    };

    // This code is only triggered when the user open the panel from the upload version button in a component
    if (targetParentComponent && targetLatestVersionComponent) {
      input.payload.parentComponentId = get(targetParentComponent, "id", "");
      input.payload.workId = get(targetParentComponent, "work[0].id", "");
      input.payload.latestVersionComponentId = targetLatestVersionComponent.id;
      input.payload.componentInput.version = computeComponentVersion(
        targetLatestVersionComponent.version
      ).increase;
      input.previousTitle = input.payload.componentInput.title;
      input.payload.componentInput.title = get(
        targetParentComponent,
        "title",
        ""
      );
      input.previousGroup = input.payload.componentGroup;
      input.payload.componentGroup = get(
        targetParentComponent,
        "componentGroup",
        ""
      );
      input.payload.componentGroupOrderWeight = get(
        targetParentComponent,
        "componentGroupOrderWeight",
        ""
      );
      input.payload.componentOrderWeight = get(
        targetParentComponent,
        "componentOrderWeight",
        ""
      );
      input.isVersion = true;
    }

    return arr.concat(input);
  }, []);
}

export function getFirstShareableComponent(
  uploadedFiles = [],
  importedFiles = []
) {
  for (let i = 0; i < uploadedFiles.length; i++) {
    const file = uploadedFiles[i];
    if (!file.isIdea) {
      return get(file, "payload.componentInput.id", "");
    }
  }

  for (let i = 0; i < importedFiles.length; i++) {
    const file = importedFiles[i];
    if (!file.isIdea) {
      return get(file, "payload.componentInput.id", "");
    }
  }
}

export function addInput({
  input,
  selectedProjectId,
  previouslyAddedFiles,
  sameSpaceComponentsByGroupAndOrdered,
}) {
  let payload = { ...input.payload, projectId: selectedProjectId };
  const targetGroupList =
    sameSpaceComponentsByGroupAndOrdered[payload.componentGroup];
  const {
    componentOrderWeight,
    componentGroupOrderWeight,
  } = getComponentGroupAndWeights({
    selectedProjectId,
    targetGroupList,
  });

  // Check if uploading multiple files as version of a single component and update the version number
  const sameComponentTarget = (previouslyAddedFiles || []).filter(
    (input) =>
      get(input, "payload.parentComponentId", "") === payload.parentComponentId
  );

  if (sameComponentTarget.length > 0) {
    let newVersion = payload.componentInput.version;
    for (let i = 0; i < sameComponentTarget.length; i++) {
      newVersion = computeComponentVersion(newVersion).increase;
    }
    const componentInput = { ...payload.componentInput, version: newVersion };
    payload = { ...payload, componentInput };
  }

  const checkedProjectId =
    payload.projectId === "Dashboard" ? "" : payload.projectId;
  let updatedPayload = {
    ...payload,
    label: input.label,
    componentOrderWeight,
    componentGroupOrderWeight,
    projectId: checkedProjectId,
  };

  if (input.isIdea) {
    if (input.isVersion) {
      if (input.isWorkRequired) {
        return createEntity("VersionOfReferenceWithWork", updatedPayload);
      }
      return createEntity("VersionOfReference", updatedPayload);
    }
    if (input.isWorkRequired) {
      return createEntity("ReferenceWithWork", updatedPayload);
    }
    return createEntity("Reference", updatedPayload);
  }

  const fileInputs =
    payload.fileInputs || (payload.fileInput ? [payload.fileInput] : undefined);
  const fileInput =
    input.isFolderUpload && payload.fileInputs
      ? get(payload.fileInputs, "[0]")
      : payload.fileInput;
  const componentPayload = {
    ...updatedPayload,
    ...(fileInput ? { fileInput } : {}),
    ...(fileInputs ? { fileInputs } : {}),
  };

  if (input.isVersion) {
    if (input.isWorkRequired) {
      return createEntity("VersionOfComponentWithWork", componentPayload);
    }
    return createEntity("VersionOfComponent", componentPayload);
  }
  if (input.isWorkRequired) {
    return createEntity("ComponentWithWork", componentPayload);
  }
  return createEntity("Component", componentPayload);
}

export function getComponentGroupAndWeights({
  selectedProjectId,
  targetGroupList,
}) {
  if (selectedProjectId === "Dashboard" || !targetGroupList) {
    return {
      componentOrderWeight: 0,
      componentGroupOrderWeight: 0,
    };
  }
  return {
    componentOrderWeight: getComponentHigherOrder(targetGroupList),
    componentGroupOrderWeight: getComponentHigherGroupOrder(targetGroupList),
  };
}

function getComponentHigherOrder(targetGroupList) {
  if (notEmpty(targetGroupList)) {
    const position = get(
      targetGroupList,
      "[0].groupLabels.componentOrderWeight",
      0
    );
    return position !== 0 ? position + 1 : 0;
  }
  return 0;
}

function getComponentHigherGroupOrder(targetGroupList) {
  if (notEmpty(targetGroupList)) {
    return get(targetGroupList, "[0].groupLabels.componentGroupOrderWeight", 0);
  }
  return 0;
}

export const NO_ID = "NO_ID";

export function getLocation(pathname) {
  const pathVars = pathname.split("/").filter((el) => el); // filter for falsy values, such as empty string
  let location = [2, 4].reduce((loc, n) => {
    const pathIdTouple = pathVars.splice(0, n);
    if (pathIdTouple[0]) {
      loc[pathIdTouple[0]] = pathIdTouple[1] || NO_ID;
    }
    return loc;
  }, {});
  return location;
}

export async function createNewIdeaWithComment({ type, payload, comment }) {
  const { personId } = payload;
  const savedReference = await createEntity(type, payload);
  if (!savedReference) {
    throw new Error(`No ${type} returned`);
  } else {
    const commentPayload = {
      text: comment,
      onId: savedReference.id,
      authorId: personId,
    };
    const savedMessage = await createEntity("Comment", commentPayload);
    savedReference.comments = [savedMessage];
    savedReference["__typename"] = type;
    if (!savedMessage) {
      throw new Error("No Comment Added");
    }
    return savedReference;
    // TODO: dispatch(fetchTasksInProject({ personId, projectId }));
  }
}
